import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AriaMark } from "@/components/AriaMark";
import { AriaPanel } from "@/components/aria/AriaPanel";
import { useAriaChat } from "@/components/aria/useAriaChat";
import { useAriaPanel } from "@/components/aria/useAriaPanel";
import { useObjectSelection } from "@/components/aria/useObjectSelection";
import { PAGE_NAMES, suggestionsFor } from "@/components/aria/constants";
import { MousePointerClick } from "lucide-react";

export const AskAria = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pageKey = location.pathname.split("/")[1] || "expenses";
  const pageName = PAGE_NAMES[pageKey] || "Aria";

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const setTabRef = useRef(null);
  const sel = useObjectSelection({
    onActivate: () => { setExpanded(false); setOpen(true); setTabRef.current?.("chat"); },
  });
  const chat = useAriaChat({
    pageName,
    selection: sel.selection,
    clearSelection: sel.clearSelection,
    onNavigate: (path) => { navigate(path); setExpanded(false); },
  });
  setTabRef.current = chat.setTab;
  const { panelWidth, dragging, startResize } = useAriaPanel({ onPopOut: () => setExpanded(true) });

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("aria:open", openHandler);
    return () => window.removeEventListener("aria:open", openHandler);
  }, []);

  useEffect(() => { if (open) chat.onOpen(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const panel = (
    <AriaPanel
      chat={chat} pageName={pageName} suggestions={suggestionsFor(pageKey)}
      selectMode={sel.selectMode} toggleSelectMode={sel.toggleSelectMode}
      selection={sel.selection} setSelection={sel.setSelection}
      expanded={expanded} onToggleExpand={() => setExpanded((e) => !e)}
      onClose={() => { setOpen(false); setExpanded(false); }}
    />
  );

  return (
    <>
      {sel.selection.length > 0 && (
        <style>{sel.selection.map((s) => `[data-aria-key="${s.key}"]{outline:2px solid #4F8EF7 !important;outline-offset:-2px;border-radius:8px;}`).join("\n")}</style>
      )}
      {sel.selectMode && (
        <div data-testid="aria-select-mode-indicator"
          className="fixed left-1/2 top-12 z-[70] flex -translate-x-1/2 items-center gap-2 rounded-full border border-[rgba(79,142,247,0.4)] bg-[#0f1420] px-4 py-1.5 text-[11.5px] text-[#cfe0fb] shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
          <MousePointerClick size={12} className="text-[#7FAEF9]" />
          Selection mode — click expenses, people, queues or policies · <span className="text-[#7FAEF9]">{sel.selection.length} selected</span> · Esc to finish
        </div>
      )}
      {!open && (
        <motion.button initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          onClick={() => setOpen(true)} data-testid="ask-aria-button"
          className="fixed bottom-20 right-4 z-[55] flex items-center gap-2 rounded-full border border-[#2a2a2a] bg-[#161616] py-2.5 pl-3.5 pr-4 text-[13px] font-medium text-white shadow-[0_10px_28px_rgba(0,0,0,0.5)] transition-colors hover:bg-[#1f1f1f] lg:bottom-6 lg:right-6">
          <AriaMark size={18} /> Ask Aria
        </motion.button>
      )}
      <AnimatePresence>
        {open && expanded && (
          <motion.div key="aria-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(0,0,0,0.65)] p-3 backdrop-blur-sm sm:p-8"
            data-testid="ask-aria-overlay">
            <motion.div initial={{ scale: 0.97, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.97, y: 10 }}
              className="flex h-full w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-[#242424] bg-[#0f0f0f] shadow-[0_30px_80px_rgba(0,0,0,0.7)]">
              {panel}
            </motion.div>
          </motion.div>
        )}
        {open && !expanded && (
          <motion.div key="aria-drawer" initial={{ x: 480 }} animate={{ x: 0 }} exit={{ x: 480 }}
            transition={dragging ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 36 }}
            style={{ width: panelWidth }}
            className="fixed bottom-0 right-0 top-0 z-[60] flex max-w-[96vw] flex-col border-l border-[#242424] bg-[#0f0f0f] shadow-[-24px_0_60px_rgba(0,0,0,0.55)]"
            data-testid="ask-aria-panel">
            <div onPointerDown={startResize} data-testid="ask-aria-resize-handle" title="Drag to resize — past halfway pops Aria out"
              className={`group absolute inset-y-0 left-0 z-10 flex w-2 cursor-ew-resize items-center justify-center transition-colors hover:bg-[rgba(79,142,247,0.25)] ${dragging ? "bg-[rgba(79,142,247,0.35)]" : ""}`}>
              <span className="h-10 w-[3px] rounded-full bg-[#2e2e2e] transition-colors group-hover:bg-[#4F8EF7]" />
            </div>
            {panel}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
